import { DetailedInsight } from '@api/api/insights';
import { KgInsight } from '@api/api/kg-insights';
import { Markdown } from '@terminal/components/markdown/Markdown';
import { useInsightsById } from '@terminal/react-query-hooks/insights/useInsight';
import { useKgInsightsById } from '@terminal/react-query-hooks/insights/useKgInsight';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePulseCitationClick } from '@terminal/views/theme-overview/tab-content/pulse-overview/hooks/usePulseCitationClick';
import { PulseDailyUpdate } from '@terminal/views/theme-overview/tab-content/pulse-overview/pulse-types';
import {
  linkDailySummaryCitations,
  stripDailySummaryCitations,
} from '@terminal/views/theme-overview/tab-content/pulse-overview/utils/linkDailySummaryCitations';
import styles from './DailyUpdateCard.module.scss';

interface DailyUpdateCardProps {
  update: PulseDailyUpdate;
  themeId?: string;
  hasCitationLinks?: boolean;
  source?: PosthogTrackingSource;
}

export const DailyUpdateCard = ({
  update,
  themeId,
  hasCitationLinks = true,
  source = POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW,
}: DailyUpdateCardProps) => {
  const kgInsightIds = [
    ...new Set(
      hasCitationLinks
        ? (update.citations ?? []).flatMap(citation =>
            citation.kg_insight_id ? [citation.kg_insight_id] : []
          )
        : []
    ),
  ];
  const scenarioInsightIds = [
    ...new Set(
      hasCitationLinks
        ? (update.citations ?? []).flatMap(citation =>
            citation.scenario_insight_id ? [citation.scenario_insight_id] : []
          )
        : []
    ),
  ];
  const kgInsightsById = useKgInsightsById(kgInsightIds);
  const scenarioInsightsById = useInsightsById(scenarioInsightIds);
  const insightsById = new Map<string, KgInsight | DetailedInsight>([
    ...kgInsightsById,
    ...scenarioInsightsById,
  ]);
  const { handleCitationClick } = usePulseCitationClick({
    themeId,
    insightsById,
    scenarioInsightIds: new Set(scenarioInsightIds),
    source,
  });
  const summary = hasCitationLinks
    ? linkDailySummaryCitations(update.summary, update.citations)
    : stripDailySummaryCitations(update.summary);

  return (
    <div className={styles.root} data-testid="pulse-daily-update">
      <div
        className={styles.paragraph}
        data-daily-update-content
        onClick={hasCitationLinks ? handleCitationClick : undefined}
      >
        <Markdown message={summary} />
      </div>
    </div>
  );
};
